// Sprint 6 (Phase 2, D48) — AggregationCard.
// Sprint ai-elements-rendering (Phase 2, T+X) — restyled to the AI Elements
// ChainOfThoughtStep language (ADR D-026): icon rail + connector + muted
// completed tone, matching ToolCallRow. Behaviour unchanged: aggregate.ts
// grouping untouched, D-msg-8 local override preserved, all testids kept.
//
// Visual sibling of `FounderToolRow`: a single collapsed row with an icon,
// humanized count headline, and a status glyph that aggregates the children.
// Expanding `▽` reveals the original list of `FounderToolRow`s — each one
// retains its own `▽` so the founder can drill into a specific tool call's
// developer detail without flipping the global toggle.

import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '../atoms/Icon';
import type { AggregationItem } from '../../lib/tool-rendering/aggregate';
import { fallbackHeadline } from '../../lib/tool-rendering/verb-map';
import { useSettingsStore } from '../../stores/settings.store';
import { useTranslation } from '../../i18n';
import { FounderToolRow } from './FounderToolRow';

export interface AggregationCardProps {
  item: AggregationItem;
  /** Map from tool_use_id → tool_result Message, used by child rows. */
  resultByToolUseId: Map<string, import('../../types').Message>;
}

export function AggregationCard({ item, resultByToolUseId }: AggregationCardProps) {
  const { t } = useTranslation();
  const developerMode = useSettingsStore((s) => s.developerMode);
  // Sprint messages-section-fixes (D-msg-8) — same per-row override
  // preservation as ToolCallRow. The local toggle survives global
  // developer-mode flips once the founder has explicitly touched it.
  const [open, setOpen] = useState<boolean>(developerMode);
  const userTouchedRef = useRef<boolean>(false);
  useEffect(() => {
    if (userTouchedRef.current) return;
    setOpen(developerMode);
  }, [developerMode]);
  const handleToggle = () => {
    userTouchedRef.current = true;
    setOpen((v) => !v);
  };

  const fallback = fallbackHeadline(item.toolName, {});
  const resolvedIconName = (fallback.iconName ?? 'Cog') as IconName;

  const isError = item.errorCount > 0;
  const isRunning = item.runningCount > 0 && item.errorCount === 0;
  // Step-language tones (chain-of-thought.tsx statusStyles) — same
  // mapping as ToolCallRow: running=foreground, done=muted, error=status.
  const headlineColor = isError
    ? 'text-status-error'
    : isRunning
      ? 'text-foreground'
      : 'text-muted-foreground';
  const iconColor = isError ? 'text-status-error' : 'text-muted-foreground';

  return (
    <div
      className="fade-in-0 slide-in-from-top-2 animate-in flex gap-2 text-body"
      data-testid="aggregation-card"
      data-tool={item.toolName}
      data-count={item.count}
      data-error-count={item.errorCount}
    >
      <div className="relative mt-1 shrink-0">
        <Icon name={resolvedIconName} className={iconColor} size={14} aria-hidden="true" />
        <div className="-mx-px absolute top-6 bottom-0 left-1/2 w-px bg-border" />
      </div>
      <div className="flex-1 min-w-0 space-y-1 overflow-hidden">
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={open}
          className="flex w-full items-center gap-2 py-0.5 rounded-md text-left transition-colors duration-100 hover:bg-accent focus:outline-none focus:ring-1 focus:ring-border"
          data-testid="aggregation-header"
        >
          <span className={`flex-1 truncate text-body ${headlineColor}`}>
            {item.headline}
          </span>
          <AggregationStatusGlyph
            isError={isError}
            isRunning={isRunning}
            errorCount={item.errorCount}
            t={t}
          />
          <span className="text-muted-foreground/45 shrink-0" aria-hidden="true">
            {open ? <Icon name="ChevronDown" size={12} /> : <Icon name="ChevronRight" size={12} />}
          </span>
        </button>
        {open && (
          <div
            className="space-y-1 pb-1"
            data-testid="aggregation-children"
          >
            {item.items.map((m) => {
              const c = m.content as { tool_use_id?: unknown };
              const toolUseId = typeof c?.tool_use_id === 'string' ? c.tool_use_id : '';
              const result = resultByToolUseId.get(toolUseId);
              return (
                <FounderToolRow
                  key={m.id}
                  toolUseMessage={m}
                  toolResultMessage={result}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AggregationStatusGlyph({
  isError,
  isRunning,
  errorCount,
  t,
}: {
  isError: boolean;
  isRunning: boolean;
  errorCount: number;
  t: (key: import('../../i18n').TKey, params?: Record<string, string | number>) => string;
}) {
  if (isRunning) {
    return (
      <Icon
        name="Loader2"
        className="shrink-0 text-muted-foreground animate-spin"
        size={14}
        aria-label={t('aggregation.running')}
      />
    );
  }
  if (isError) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-status-error"
        aria-label={t('aggregation.errored', { count: errorCount })}
      >
        <Icon name="X" size={14} />
        {errorCount > 1 && (
          <span className="text-2xs font-medium" /* design-discipline: kept as text-2xs — tiny error badge */>{errorCount}</span>
        )}
      </span>
    );
  }
  return (
    <Icon
      name="Check"
      className="shrink-0 text-status-success"
      size={14}
      aria-label={t('aggregation.completed')}
    />
  );
}
